import React, { useEffect, useRef, useState } from 'react';
import './AddEventPopup.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import CustomTogglebox from '../input/CustomTogglebox';
import ParticipantGroupPriceMatrix, {
  settingsToMatrixRows, validateMatrixRows,
} from '../participant-group/ParticipantGroupPriceMatrix';
import AddOnChips from '../add-on/AddOnChips';
import { formatMaxPerEvent } from '../add-on/AddOnRowsEditor';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { parsePriceInput } from '../../util/eventPrices';
import { addOnColorKey, formatAddOnPrice, formatGroupPrices } from '../../util/addOns';
import AssignableEventList from './AssignableEventList';
import ProcessNotice from './ProcessNotice';
import { apiErrorMessage, eventNouns, parseIntInput, processLocationName } from './processUtils';

export const ADD_EVENT_POPUP = 'eventprocessadd';

// Price defaults of the process (template chain) → matrix rows. Shown as normal rows (no "Standard"
// chip): in a create form every value is a prefill.
function rowsFromDefaults(priceDefaults) {
  const rows = settingsToMatrixRows(priceDefaults);
  Object.keys(rows).forEach(id => { rows[id] = { ...rows[id], materialized: true }; });
  return rows;
}

const countLabel = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Success text of "Zuordnen" (AddEventsResult): attaching applies the process add-ons to the attached events.
function assignedMessage(result, count, nouns) {
  const head = `${count === 1 ? nouns.one : `${count} ${nouns.many}`} zugeordnet`;
  const removedBookings = Number(result?.removedBookingCount ?? 0);
  const removedWishes = Number(result?.removedRegistrationWishCount ?? 0);
  const repriced = Number(result?.repricedParticipantCount ?? 0);
  const parts = [
    removedBookings > 0 ? `${countLabel(removedBookings, 'Zusatz-Buchung', 'Zusatz-Buchungen')} entfernt` : null,
    removedWishes > 0 ? `${countLabel(removedWishes, 'Anmeldewunsch', 'Anmeldewünsche')} angepasst` : null,
    repriced > 0 ? `${countLabel(repriced, 'Preis', 'Preise')} neu berechnet` : null,
  ].filter(Boolean);
  return parts.length > 0 ? `${head} – ${parts.join(', ')}` : head;
}

/**
 * "+ Event" of a process: assign existing compatible events (GET /EventProcess/GetUnassigned?processId= —
 * the server also filters by the process location) or create a new one directly in the process
 * (POST /EventProcess/{id}/CreateEvent). The location is the process location (read-only, the event
 * always takes it). Entry prices per group are prefilled from process.priceDefaults (no extra request);
 * releases are set by the process steps. The sign-up add-ons belong to the process (section "Zusätze") and
 * are only shown here; attaching an event applies them to it (bookings of add-ons the process lacks are
 * removed, changed prices repriced — the counts come back with the AddEvents response).
 */
function AddEventPopup({ process, template, onProcess }) {
  const { isPopupOpen, popupData, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const isOpen = isPopupOpen && popupData.name === ADD_EVENT_POPUP;
  const { active: activeGroups, loaded: groupsLoaded } = useParticipantGroups();

  const [unassigned, setUnassigned] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [assignIds, setAssignIds] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [autoPaid, setAutoPaid] = useState(true);
  const [priceRows, setPriceRows] = useState({});

  const dateRef = useRef();
  const timeRef = useRef();
  const timeEndRef = useRef();
  const maxRef = useRef();
  // Read on open only — later process updates (e.g. after assigning) must not reset the form.
  const processRef = useRef(process);
  processRef.current = process;
  const templateRef = useRef(template);
  templateRef.current = template;

  useEffect(() => {
    if (!isOpen) return;
    const p = processRef.current;
    if (!p) return;
    setAssignIds([]);
    setError('');
    // Default "Bei Anwesenheit automatisch bezahlt": the process's own snapshot (the backend default of
    // CreateEvent) once the API returns it, else the template's current value (as before: off for
    // link sign-up). The toggle value is always sent, so the event gets what the form shows.
    setAutoPaid(p.autoPaidOnPresenceDefault ?? templateRef.current?.autoPaidOnPresenceDefault ?? !p.linkSignup);
    setPriceRows(rowsFromDefaults(p.priceDefaults));
    setUnassigned([]);
    setLoadingUnassigned(true);
    // processId: compatibility by the process's own (materialized) rules — the same check as AddEvents.
    api.get(`/EventProcess/GetUnassigned?processId=${p.id}`)
      .then(r => setUnassigned(Array.isArray(r.data) ? r.data : []))
      .catch(() => setUnassigned([]))
      .finally(() => setLoadingUnassigned(false));
  }, [isOpen]);

  if (!process) return null;
  const nouns = eventNouns(process);
  const matrixGroups = activeGroups;
  const processAddOns = process.addOns ?? [];
  const addOnItems = processAddOns.map(a => ({
    key: `addon-${a.id ?? a.addOnId}`,
    label: a.addOnName,
    // Current catalog color of the process row's add-on ('neutral' without one).
    colorKey: addOnColorKey(a.colorKey),
    tooltip: [
      `${a.addOnName}: ${formatAddOnPrice(a.price)}`,
      formatGroupPrices(a, activeGroups) || null,
      formatMaxPerEvent(a.maxQuantity, nouns.one),
      a.bookableOnHomepage ? 'auch über die Homepage' : 'nur durch die Orga',
    ].filter(Boolean).join(' · '),
  }));
  // Fixed by the process.
  const locationName = processLocationName(process);

  const toggleAssign = (id) => {
    setAssignIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleAssign = () => {
    if (saving || assignIds.length === 0) return;
    setSaving(true);
    setError('');
    startSuccessBarLoading(`${nouns.many} werden zugeordnet`);
    api.put(`/EventProcess/${process.id}/AddEvents`, { eventIds: assignIds })
      .then(response => {
        // AddEventsResult { process, repricedParticipantCount, removedBookingCount, removedRegistrationWishCount }.
        const result = response.data ?? {};
        startSuccessBarSuccess(assignedMessage(result, assignIds.length, nouns));
        onProcess(result.process ?? result, { resetView: false });
        closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorMessage(err, 'Zuordnen fehlgeschlagen.'));
      })
      .finally(() => setSaving(false));
  };

  const handleCreate = () => {
    if (saving) return;
    const date = dateRef.current?.value;
    if (!date) { setError('Bitte ein Datum wählen.'); return; }
    if (!groupsLoaded || matrixGroups.length === 0) { setError('Die Gruppen werden noch geladen – bitte einen Moment warten.'); return; }

    const priceError = validateMatrixRows(priceRows, matrixGroups);
    if (priceError) { setError(priceError); return; }

    const maxPlayers = parseIntInput(maxRef.current, 1, 200, 24);
    if (maxPlayers == null) {
      setError('Max. Spieler: bitte eine ganze Zahl von 1 bis 200 angeben.');
      return;
    }
    // Entry prices of every group (prefilled from the template chain). Releases come from the process steps.
    const participantGroupSettings = matrixGroups.map(g => ({
      participantGroupId: g.id,
      entryPrice: parsePriceInput(priceRows[g.id]?.entry),
    }));

    // Template, rules, location and sign-up add-ons come from the process.
    const body = {
      date,
      time: timeRef.current?.value ? timeRef.current.value + ':00' : null,
      timeEnd: timeEndRef.current?.value ? timeEndRef.current.value + ':00' : null,
      maxPlayers,
      autoPaidOnPresence: autoPaid,
      participantGroupSettings,
    };

    setSaving(true);
    setError('');
    startSuccessBarLoading(`${nouns.one} wird erstellt`);
    // One request creates the event inside the process (releases of executed release steps are
    // caught up) — no orphaned event if assigning failed.
    api.post(`/EventProcess/${process.id}/CreateEvent`, body)
      .then(response => {
        startSuccessBarSuccess(`${nouns.one} erstellt & hinzugefügt`);
        onProcess(response.data, { resetView: false });
        closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorMessage(err, `${nouns.one} konnte nicht erstellt werden.`));
      })
      .finally(() => setSaving(false));
  };

  const templateName = process.processTemplateName || template?.name;

  return (
    <Popup name={ADD_EVENT_POPUP} headerText={`${nouns.one} hinzufügen`} type={'info'} withConfirm={false} withCancel wide>
      <div className={'addEventForm'}>
        {error && <ProcessNotice tone={'error'}>{error}</ProcessNotice>}

        <section className={'addEventSection'}>
          <h3>Bestehende {nouns.many} zuordnen</h3>
          <AssignableEventList
            events={unassigned}
            selectedIds={assignIds}
            onToggle={toggleAssign}
            loading={loadingUnassigned}
            emptyText={`Keine passenden ${nouns.many} ohne Prozess am Ort „${locationName}“ vorhanden.`}
          />
          {unassigned.length > 0 && (
            <p className={'formFieldHint'}>
              Zugeordnete {nouns.many} übernehmen die Zusätze dieses Prozesses: Buchungen von Zusätzen, die der Prozess
              nicht anbietet, werden entfernt; offene Preise werden neu berechnet (bezahlte und manuell gesetzte bleiben).
            </p>
          )}
          {unassigned.length > 0 && (
            <div className={'addEventActions'}>
              <ActionButton icon={'link'} label={'Zuordnen'} type={3} onClick={handleAssign} disabled={assignIds.length === 0 || saving} />
            </div>
          )}
        </section>

        <div className={'addEventDivider'}><span>oder</span></div>

        <section className={'addEventSection'}>
          <h3>{nouns.one === 'Spieltag' ? 'Neuen Spieltag anlegen' : 'Neues Event anlegen'}</h3>
          {process.linkSignup && (
            <p className={'formFieldHint'}>
              Den Einladungslink für Gäste findest du nach dem Anlegen in der Event-Liste (Menü „Events") im Bearbeiten-Popup des Events.
            </p>
          )}
          <div className={'addEventGrid'}>
            <div className={'formFieldWrapper'}>
              <label className={'formFieldLabel'}>Datum</label>
              <input ref={dateRef} type={'date'} required />
            </div>
            <div className={'formFieldWrapper'}>
              <label className={'formFieldLabel'}>Beginn</label>
              <input ref={timeRef} type={'time'} defaultValue={'10:00'} />
            </div>
            <div className={'formFieldWrapper'}>
              <label className={'formFieldLabel'}>Ende</label>
              <input ref={timeEndRef} type={'time'} defaultValue={'16:00'} />
            </div>
            <div className={'formFieldWrapper'}>
              <label className={'formFieldLabel'}>Max. Spieler</label>
              <input ref={maxRef} type={'number'} min={1} max={200} defaultValue={24} />
            </div>
            <div className={'addEventFull addEventLocation'}>
              <span className={'material-symbols-rounded addEventLocationIcon'} aria-hidden={'true'}>location_on</span>
              <span>Ort: <b>{locationName}</b> (vom Prozess)</span>
            </div>
            {/* Sign-up add-ons: shared by all events of the process, maintained in the process section "Zusätze". */}
            <div className={'addEventFull addEventAddOns'}>
              <span className={'addEventSubheading'}>Zusätze</span>
              <div className={'addEventAddOnsBox'}>
                <span className={'material-symbols-rounded addEventLocationIcon'} aria-hidden={'true'}>inventory_2</span>
                {addOnItems.length > 0
                  ? <AddOnChips items={addOnItems} ariaLabel={'Zusätze des Prozesses'} />
                  : <span className={'addEventAddOnsNone'}>Keine Zusätze in diesem Prozess.</span>}
              </div>
              <p className={'formFieldHint'}>Zusätze werden im Prozess gepflegt.</p>
            </div>
            <div className={'addEventFull addEventPrices'}>
              <span className={'addEventSubheading'}>Eintritt je Gruppe</span>
              {!groupsLoaded ? (
                <p className={'formFieldHint'}>Gruppen werden geladen …</p>
              ) : (
                <ParticipantGroupPriceMatrix
                  groups={matrixGroups}
                  rows={priceRows}
                  onChange={(id, patch) => setPriceRows(prev => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...patch } }))}
                  disabled={saving}
                />
              )}
              <p className={'formFieldHint'}>
                Vorbelegt mit den Standardpreisen{templateName ? ` der Vorlage „${templateName}"` : ''}.
                Für welche Gruppen das Event sichtbar ist, legen die Freigabe-Schritte des Prozesses fest.
              </p>
            </div>
            <div className={'addEventFull'}>
              <CustomTogglebox
                label={'Bei Anwesenheit automatisch bezahlt'}
                placeholder={autoPaid ? 'Anwesend = bezahlt' : 'Manuell markieren'}
                checked={autoPaid}
                onChange={e => setAutoPaid(e.target.checked)}
              />
            </div>
          </div>
          <div className={'addEventActions'}>
            <ActionButton icon={'add'} label={'Anlegen & hinzufügen'} type={2} onClick={handleCreate} disabled={saving} />
          </div>
        </section>
      </div>
    </Popup>
  );
}

export default AddEventPopup;
